import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import Select from "react-select";
import { useReactToPrint } from "react-to-print";
import XlsxPopulate from "xlsx-populate";
import { saveAs } from "file-saver";

// reactstrap components
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  FormGroup,
  Form,
  Row,
  Col,
} from "reactstrap";

// helpers
import { formateDateandTime } from "helpers/formats";

// services
import { api_POST } from "../../../services/api_service.js";
import { api_GET } from "../../../services/api_service.js";
import { showMessage } from "../../../services/message.js";

//Context
import { useAuth } from "../../../context/auth.js";

const fmt = (date) =>
  date ? new Date(date).toLocaleDateString("pt-BR") : "-";

export default function RelatorioEtapas({ handleReturn }) {
  const { setLoading } = useAuth();

  const { register, handleSubmit, setValue, clearErrors } = useForm({
    mode: "onBlur",
    defaultValues: {
      categoria: "TODOS",
      tipoCadastro: "TODOS",
      tipoRequerimento: "TODOS",
      etapa: "TODOS",
    },
  });

  const [relatorio, set_relatorio] = useState(null);

  const [categoria, setCategoria] = useState("TODOS");
  const [tipoCadastro, setTipoCadastro] = useState("TODOS");
  const [tipoRequerimento, setTipoRequerimento] = useState("TODOS");
  const [etapa, setEtapa] = useState("TODOS");

  const [setupSolicitacoes, set_SetupSolicitacoes] = useState([]);
  const [optionsTipoRequerimento, set_OptionsTipoRequerimento] = useState([
    { value: "TODOS", label: "TODOS" },
  ]);
  const [optionsEtapa, set_OptionsEtapa] = useState([
    { value: "TODOS", label: "TODOS" },
  ]);

  const componentRef = React.useRef();

  const [optionsCategorias] = useState([
    { value: "TODOS", label: "TODOS" },
    { value: "RCC", label: "RCC" },
    { value: "RGG", label: "RGG" },
    { value: "RSS", label: "RSS" },
  ]);

  const optionsTipoCadastro = useMemo(() => {
    const ativas = setupSolicitacoes.filter((s) => s.status === "Ativo");
    const filtradas = categoria === "TODOS"
      ? ativas
      : ativas.filter((s) => s.categoria?.sigla === categoria);

    // Duas solicitações distintas podem compartilhar a mesma Descricao —
    // como o filtro é por Descricao, mantemos só uma opção por texto.
    const descricoesUnicas = Array.from(new Set(filtradas.map((s) => s.descricao)));

    return [
      { value: "TODOS", label: "TODOS" },
      ...descricoesUnicas.map((d) => ({ value: d, label: d })),
    ];
  }, [setupSolicitacoes, categoria]);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    copyStyles: true,
  });

  const handleCategoria = (value) => {
    setCategoria(value);
    setValue("categoria", value, true);
    clearErrors("categoria");

    // Tipo de Cadastro é dependente da Categoria — volta para TODOS ao trocar
    setTipoCadastro("TODOS");
    setValue("tipoCadastro", "TODOS", true);
  };

  const handleTipoCadastro = (value) => {
    setTipoCadastro(value);
    setValue("tipoCadastro", value, true);
    clearErrors("tipoCadastro");
  };

  const handleTipoRequerimento = (value) => {
    setTipoRequerimento(value);
    setValue("tipoRequerimento", value, true);
    clearErrors("tipoRequerimento");
  };

  const handleEtapa = (value) => {
    setEtapa(value);
    setValue("etapa", value, true);
    clearErrors("etapa");
  };

  const buildFiltro = (data) => ({
    categoria: data.categoria || "TODOS",
    tipoCadastro: data.tipoCadastro || "TODOS",
    tipoRequerimento: data.tipoRequerimento || "TODOS",
    etapa: data.etapa || "TODOS",
  });

  const fetchRelatorio = async (filtro) => {
    try {
      setLoading(true);
      const { data } = await api_POST("relatorio/RelatorioEtapas", filtro);
      return data;
    } catch (err) {
      showMessage("Aviso", err.message, "error", null);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    const resultado = await fetchRelatorio(buildFiltro(data));
    set_relatorio(resultado || null);
  };

  const handleExcel = async () => {
    const resultado = await fetchRelatorio(
      buildFiltro({ categoria, tipoCadastro, tipoRequerimento, etapa })
    );
    if (!resultado) return;

    const lista = resultado.requerimentos || [];

    const header = [
      "Nº Requerimento", "Status Requerimento", "Categoria", "Tipo de Cadastro", "Tipo de Requerimento", "Etapa",
      "Data de Aprovação", "Data da Última Reprovação", "Total de Reprovações",
      "Data da Última Modificação", "Emissão da Última Guia",
      "Razão Social", "Nome Fantasia", "CNPJ/CPF", "Tipo de Pessoa",
      "Endereço", "Código de Cadastro", "Responsável Legal", "Ramo de Atividade",
      "Telefone", "E-mail", "Modalidade", "Transportador", "SubPrefeitura",
      "Responsável pela última ação",
    ];

    const rows = lista.map((m) => [
      m.numeroRequerimento, m.statusRequerimento, m.categoria, m.tipoCadastro, m.tipoRequerimento, m.etapa,
      fmt(m.dataAprovacao), fmt(m.dataUltimaReprovacao), m.totalReprovacoes,
      fmt(m.dataUltimaModificacao), fmt(m.dataEmissaoUltimaGuia),
      m.razaoSocial, m.nomeFantasia, m.cpf_Cnpj, m.tipoPessoa,
      m.endereco, m.codigoCadastro, m.responsavelLegal, m.ramoAtividade,
      m.telefone, m.email, m.modalidade, m.transportador, m.subprefeitura,
      m.responsavelAnalise || "-",
    ]);

    XlsxPopulate.fromBlankAsync().then((workbook) => {
      const dia = new Date();
      const sheet = workbook.sheet(0).name("Etapas");
      sheet.cell("A1").value([header, ...rows]);
      sheet.row(1).style("bold", true);
      return workbook.outputAsync().then((res) => {
        saveAs(res, `RelatorioEtapas_${dia.getDate()}${dia.getMonth() + 1}${dia.getFullYear()}.xlsx`);
      });
    });
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api_GET("setupsolicitacao"),
      api_GET("relatorio/RelatorioEtapasFiltros"),
    ])
      .then(([setupSolicitacaoResult, filtrosResult]) => {
        set_SetupSolicitacoes(setupSolicitacaoResult.data || []);

        const filtros = filtrosResult.data || {};
        set_OptionsTipoRequerimento([
          { value: "TODOS", label: "TODOS" },
          ...(filtros.tiposRequerimento || []).map((t) => ({ value: t, label: t })),
        ]);
        set_OptionsEtapa([
          { value: "TODOS", label: "TODOS" },
          ...(filtros.etapas || []).map((e) => ({ value: e, label: e })),
        ]);
      })
      .catch((err) => showMessage("Aviso", err.message, "error", null))
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <div className="content">
        <Row>
          <Col md="6" className="noPrint">
            <Button color="Primary" onClick={handleReturn}>
              <i className="fas fa-chevron-left"></i> Voltar
            </Button>
          </Col>
          {relatorio != null && (
            <Col md="6" className="text-right noPrint">
              <Button color="danger" onClick={handlePrint}>
                <i className="fas fa-print"></i>
              </Button>
            </Col>
          )}
        </Row>

        {/* Formulário de filtro */}
        {relatorio == null && (
          <Row>
            <Col md="12">
              <Card>
                <CardHeader>
                  <Row>
                    <Col md="9">
                      <h4 className="mb-0 mt-0">Relatório de Etapas</h4>
                      <label className="rel-subtitle">
                        Relatório de Etapas dos Requerimentos.
                      </label>
                    </Col>
                  </Row>
                </CardHeader>
                <CardBody>
                  <Form onSubmit={handleSubmit(onSubmit)}>
                    <Row className="noPrint">
                      {/* Categoria */}
                      <Col lg="6" md="12">
                        <FormGroup>
                          <label className="form-control-label mt-1">
                            Categoria:
                          </label>
                          <Select
                            options={optionsCategorias}
                            name="categoria"
                            onChange={(e) => handleCategoria(e.value)}
                            value={optionsCategorias.find((o) => o.value === categoria)}
                            placeholder="Selecione"
                            ref={(e) =>
                              register({ name: "categoria" }, { required: true })
                            }
                          />
                        </FormGroup>
                      </Col>

                      {/* Tipo de Cadastro */}
                      <Col lg="6" md="12">
                        <FormGroup>
                          <label className="form-control-label mt-1">
                            Tipo de Cadastro:
                          </label>
                          <Select
                            options={optionsTipoCadastro}
                            name="tipoCadastro"
                            onChange={(e) => handleTipoCadastro(e.value)}
                            value={optionsTipoCadastro.find((o) => o.value === tipoCadastro)}
                            placeholder="Selecione"
                            ref={(e) =>
                              register({ name: "tipoCadastro" }, { required: true })
                            }
                          />
                        </FormGroup>
                      </Col>

                      {/* Tipo de Requerimento */}
                      <Col lg="6" md="12">
                        <FormGroup>
                          <label className="form-control-label mt-1">
                            Tipo de Requerimento:
                          </label>
                          <Select
                            options={optionsTipoRequerimento}
                            name="tipoRequerimento"
                            onChange={(e) => handleTipoRequerimento(e.value)}
                            value={optionsTipoRequerimento.find((o) => o.value === tipoRequerimento)}
                            placeholder="Selecione"
                            ref={(e) =>
                              register({ name: "tipoRequerimento" }, { required: true })
                            }
                          />
                        </FormGroup>
                      </Col>

                      {/* Etapa */}
                      <Col lg="6" md="12">
                        <FormGroup>
                          <label className="form-control-label mt-1">
                            Etapa:
                          </label>
                          <Select
                            options={optionsEtapa}
                            name="etapa"
                            onChange={(e) => handleEtapa(e.value)}
                            value={optionsEtapa.find((o) => o.value === etapa)}
                            placeholder="Selecione"
                            ref={(e) =>
                              register({ name: "etapa" }, { required: true })
                            }
                          />
                        </FormGroup>
                      </Col>

                      <Col lg="2" md="2" sm="12">
                        <Button color="info" className="btn-mobile" type="submit">
                          Gerar Relatório
                        </Button>
                      </Col>
                      <Col lg="2" md="2" sm="12">
                        <Button
                          color="danger"
                          className="btn-mobile"
                          type="button"
                          onClick={handleExcel}
                        >
                          Gerar Excel
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        {/* Impressão */}
        {relatorio && (
          <Row>
            <Col md="12">
              <Card>
                <CardBody>
                  <ComponentToPrint relatorio={relatorio} ref={componentRef} />
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

      </div>
    </>
  );
}

/* ─── Componente de Impressão ─── */
export const ComponentToPrint = React.forwardRef((props, ref) => {
  const relatorio = props.relatorio;

  return (
    <div ref={ref}>
      <div className="row">
        <div className="col-9">
          <h4 className="mb-0 mt-0">Relatório Detalhado de Etapas dos Requerimentos</h4>
        </div>
        <div className="col-3 text-right">
          <label>Gerado em {formateDateandTime(new Date())}</label>
        </div>
      </div>

      {relatorio != null && (
        <>

          <div className="row mt-2 mb-2">
            <div className="col-12">
              <span><b>Total Geral:</b> {relatorio.total}</span>
            </div>
          </div>

          {(relatorio.requerimentos || []).map((m, key) => (
            <div className="rel-group" key={key}>
              <div className="row rel-item">

                {/* Identificação */}
                <div className="col-6">
                  <span><b>Nº Requerimento: </b></span>
                  <span>{m.numeroRequerimento || "-"}</span>
                </div>
                <div className="col-6">
                  <span><b>Código de Cadastro: </b></span>
                  <span>{m.codigoCadastro || "-"}</span>
                </div>
                <div className="col-6">
                  <span><b>Status Requerimento: </b></span>
                  <span>{m.statusRequerimento || "-"}</span>
                </div>

                {/* Categoria, tipo e etapa */}
                <div className="col-4">
                  <span><b>Categoria: </b></span>
                  <span>{m.categoria || "-"}</span>
                </div>
                <div className="col-4">
                  <span><b>Tipo de Cadastro: </b></span>
                  <span>{m.tipoCadastro || "-"}</span>
                </div>
                <div className="col-4">
                  <span><b>Tipo de Requerimento: </b></span>
                  <span>{m.tipoRequerimento || "-"}</span>
                </div>
                <div className="col-4">
                  <span><b>Etapa: </b></span>
                  <span>{m.etapa || "-"}</span>
                </div>

                {/* Datas conforme status */}
                {m.dataAprovacao && (
                  <div className="col-6">
                    <span><b>Data de Aprovação: </b></span>
                    <span>{fmt(m.dataAprovacao)}</span>
                  </div>
                )}
                {m.dataUltimaReprovacao && (
                  <>
                    <div className="col-6">
                      <span><b>Data da Última Reprovação: </b></span>
                      <span>{fmt(m.dataUltimaReprovacao)}</span>
                    </div>
                    <div className="col-6">
                      <span><b>Total de Reprovações: </b></span>
                      <span>{m.totalReprovacoes}</span>
                    </div>
                  </>
                )}
                {m.dataUltimaModificacao && !m.dataAprovacao && !m.dataUltimaReprovacao && (
                  <div className="col-6">
                    <span><b>Última Modificação: </b></span>
                    <span>{fmt(m.dataUltimaModificacao)}</span>
                  </div>
                )}
                {m.dataEmissaoUltimaGuia && (
                  <div className="col-6">
                    <span><b>Emissão da Última Guia: </b></span>
                    <span>{fmt(m.dataEmissaoUltimaGuia)}</span>
                  </div>
                )}

                {/* Dados cadastrais */}
                <div className="col-6">
                  <span><b>Razão Social: </b></span>
                  <span>{m.razaoSocial || "-"}</span>
                </div>
                <div className="col-6">
                  <span><b>Nome Fantasia: </b></span>
                  <span>{m.nomeFantasia || "-"}</span>
                </div>
                <div className="col-4">
                  <span><b>CNPJ/CPF: </b></span>
                  <span>{m.cpf_Cnpj || "-"}</span>
                </div>
                <div className="col-4">
                  <span><b>Tipo de Pessoa: </b></span>
                  <span>{m.tipoPessoa || "-"}</span>
                </div>
                <div className="col-4">
                  <span><b>Ramo de Atividade: </b></span>
                  <span>{m.ramoAtividade || "-"}</span>
                </div>
                <div className="col-8">
                  <span><b>Endereço: </b></span>
                  <span>{m.endereco || "-"}</span>
                </div>
                <div className="col-4">
                  <span><b>SubPrefeitura: </b></span>
                  <span>{m.subprefeitura || "-"}</span>
                </div>
                <div className="col-6">
                  <span><b>Responsável Legal: </b></span>
                  <span>{m.responsavelLegal || "-"}</span>
                </div>
                <div className="col-3">
                  <span><b>Telefone: </b></span>
                  <span>{m.telefone || "-"}</span>
                </div>
                <div className="col-6">
                  <span><b>E-mail: </b></span>
                  <span>{m.email || "-"}</span>
                </div>

                {/* Campos específicos de RGG */}
                {!!m.modalidade && (
                  <div className="col-6">
                    <span><b>Modalidade: </b></span>
                    <span>{m.modalidade}</span>
                  </div>
                )}
                {!!m.transportador && (
                  <div className="col-6">
                    <span><b>Transportador: </b></span>
                    <span>{m.transportador}</span>
                  </div>
                )}

                {/* Responsável SP Regula */}
                {!!m.responsavelAnalise && (
                  <div className="col-8">
                    <span><b>Responsável pela última ação: </b></span>
                    <span>{m.responsavelAnalise}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
});
